import { useEffect, useState } from 'react';
import Dialog from '../Journal/Dialog';
import Button from '../Button';
import Chevron from '../../assets/icons/chevron-down-current-color.svg?react';
import ExpandableContent from '../Journal/ExpandableContent';
import { eventType, Mixpanel } from '../../utils/mixpanel';

type Props = {
  minWidth: number;
  minHeight: number;
  absoluteMinWidth?: number;
};

export default function ResolutionBlocker({ minWidth, minHeight, absoluteMinWidth = 900 }: Props) {
  const [blocked, setBlocked] = useState(false);
  const [suggestions, setSuggestions] = useState<React.ReactNode[]>([]);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const scalingAdjustedWidth = window.devicePixelRatio * window.screen.width;
      const screenPixelRatio = (window.outerWidth - 8) / window.innerWidth;
      const isProbablyAtDefaultZoom = screenPixelRatio > 0.92 && screenPixelRatio <= 1.1;

      setBlocked(
        window.innerWidth < minWidth || window.innerHeight < minHeight || window.innerWidth < absoluteMinWidth
      );

      setSuggestions([
        window.screen.width > window.outerWidth && (
          <p>Maximera webbläsarfönstret för att se till att utnyttja all tillgänglig skärmyta.</p>
        ),
        !isProbablyAtDefaultZoom && (
          <div>
            <p>Din webbläsare kan vara inzoomad. Du kan återställa zoomnivån genom att trycka:</p>
            <ul className="stack-0 ml-10">
              <li>
                <strong>Windows:</strong> <kbd>Ctrl</kbd>+<kbd>0</kbd>
              </li>
              <li>
                <strong>Mac:</strong>
                <kbd>Command (Cmd)</kbd>+<kbd>0</kbd>
              </li>
            </ul>
          </div>
        ),
        isProbablyAtDefaultZoom &&
          window.devicePixelRatio !== 1 &&
          scalingAdjustedWidth >= minWidth &&
          window.navigator.platform.includes('Win') && (
            <div>
              <p>Läs här om hur du ändrar skalningsfaktorn i ditt operativsystem:</p>
              <ul className="stack-0 ml-10">
                <li>
                  <strong>Windows:</strong>{' '}
                  <a
                    href="https://support.microsoft.com/sv-se/windows/%C3%A4ndra-sk%C3%A4rmuppl%C3%B6sningen-i-windows-5effefe3-2eac-e306-0b5d-2073b765876b#ID0EDFBBDBD=Windows_11"
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    Ändra skärmupplösning och -layout i Windows
                  </a>
                </li>
              </ul>
            </div>
          )
      ]);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClose = () => {
    setBlocked(false);
    Mixpanel.track(eventType.closeResolutionWarning);
  };

  const validSuggestions = suggestions.filter((s) => !!s);

  return (
    <Dialog
      isOpen={blocked}
      title="Din skärm är för liten"
      onClose={() => {}}
      type="danger"
      actions={window.innerWidth >= absoluteMinWidth ? <Button onClick={handleClose}>OK</Button> : undefined}
      nonDismissable
    >
      <p>
        Clinic är optimerat för en skärmstorlek på minst <strong>1440x900</strong> pixlar. En mindre skärm kan påverka
        både utseende och funktionalitet. För bästa upplevelse, använd en större skärm eller kontakta din närmaste chef
        för hjälp.
      </p>
      {validSuggestions.length ? (
        <>
          <p>
            <Button variant="text" onClick={() => setSuggestionsExpanded(!suggestionsExpanded)}>
              Vad kan jag göra? <Chevron className={suggestionsExpanded ? 'rotate-180' : undefined} />
            </Button>
          </p>
          <ExpandableContent expanded={suggestionsExpanded}>
            {validSuggestions.map((s, i) => (
              <div key={i}>{s}</div>
            ))}
          </ExpandableContent>
        </>
      ) : null}
    </Dialog>
  );
}
